'use client';
import { type ReactNode } from 'react';
import { useTranslations as useTranslations$1 } from 'next-intl';
import type { DocsTocEntry } from '@/lib/docs';
import { LearnHeader } from '../learn/LearnHeader';
import { LearnSidebar } from '../learn/LearnSidebar';
import { DocsTableOfContents } from './DocsTableOfContents';
/**
 * The Learn/Docs layout shell: the Learn-specific top bar (Docs/Tutorials tabs +
 * search), a left nav sidebar, the article column, an optional right-hand
 * table of contents, and the floating Ask AI panel.
 *
 * It renders its own `LearnHeader` (which carries the Discord / Ask AI / account
 * actions) and deliberately does NOT call `useAppHeader`,
 * so the shared app header stays hidden across the Learn surface — making it read
 * like the standalone Learn site. Matches martini-learn's two-column DocsShell.
 *
 * `toc` adds a third, scroll-tracking column for the long developer references
 * (Generation API, Workflows API, MCP); table-driven articles are short enough
 * to leave it out.
 */ export function DocsShell({ children, hideSidebar = false, toc }: {
    children: ReactNode;
    hideSidebar?: boolean;
    toc?: DocsTocEntry[];
}) {
    const t = useTranslations$1();
    // The Learn landing (`/learn`) hides the nav sidebar and centers the column so
    // it reads as a standalone overview; content pages keep the two-column layout.
    const columnClass = hideSidebar ? 'mx-auto max-w-[800px]' : 'max-w-[800px]';
    const hasToc = !!toc && toc.length > 0;
    // Three columns need more room than the two-column article layout was sized for.
    const frameClass = hasToc ? 'max-w-[1500px]' : 'max-w-[1360px]';
    return (<div className="min-h-screen bg-background">
      <LearnHeader/>

      { /* Nav sidebar + article (+ table of contents); sidebar omitted on the landing */ }
      <div className={`mx-auto flex ${frameClass} gap-0`}>
        {!hideSidebar && (<aside className="no-scrollbar sticky top-12 hidden h-[calc(100vh-48px)] w-[224px] shrink-0 overflow-y-auto px-3 py-6 lg:block">
            <LearnSidebar/>
          </aside>)}

        <main className="min-w-0 flex-1">
          <div className="px-6 pb-24 pt-10 sm:px-10 lg:px-14">
            <div className={columnClass}>{children}</div>
          </div>
          <footer className="px-6 py-6 sm:px-10 lg:px-14">
            <p className={`text-xs text-muted-foreground/40 ${hideSidebar ? 'mx-auto max-w-[800px]' : ''}`}>
              {t("ZcZL-X", {
        year: String(new Date().getFullYear())
    })}
            </p>
          </footer>
        </main>

        {hasToc && (<aside className="no-scrollbar sticky top-12 hidden h-[calc(100vh-48px)] w-[220px] shrink-0 overflow-y-auto py-10 pe-6 xl:block">
            <DocsTableOfContents entries={toc}/>
          </aside>)}
      </div>
    </div>);
}
